/**
 * Formatting utilities for Mint a Milli
 * Centralizes all number/currency formatting to eliminate duplicates
 */

/**
 * Format a number as Indian Rupees currency
 * @param {number} value - The amount to format
 * @param {object} options - Intl.NumberFormat options override
 * @returns {string} Formatted currency string (e.g., "₹1,234.56")
 */
export const formatCurrency = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) return '₹0.00';
  
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  }).format(value);
};

/**
 * Format currency without decimals (for quick amounts)
 * @param {number} value - The amount to format
 * @returns {string} Formatted currency string (e.g., "₹1,234")
 */
export const formatCurrencyCompact = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '₹0';
  
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

/**
 * Format a number as Milli amount
 * @param {number} value - The milli amount to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted milli string (e.g., "1,234.56")
 */
export const formatMilli = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '0.00';
  
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * Format a generic number with Indian locale
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * Format a number as percentage
 * @param {number} value - The decimal value (0.15 = 15%)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string (e.g., "15.00%")
 */
export const formatPercent = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '0%';
  
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * Format quick amount for buttons (e.g., ₹1k, ₹10k)
 * @param {number} amount - The amount
 * @returns {string} Compact formatted string
 */
export const formatQuickAmount = (amount) => {
  if (amount >= 100000) {
    return `₹${amount / 100000}L`;
  }
  if (amount >= 1000) {
    return `₹${amount / 1000}k`;
  }
  return `₹${amount}`;
};

/**
 * Format grams from milli amount
 * @param {number} milliAmount - The milli amount
 * @returns {string} Formatted gram string (e.g., "0.0012")
 */
export const formatGrams = (milliAmount) => {
  if (milliAmount === null || milliAmount === undefined || isNaN(milliAmount)) return '0.0000';
  return (milliAmount / 1000).toFixed(4);
};
